import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
// Service data — shared with scripts/prerender.js (static HTML for search engines)
import SERVICES from '../data/services.json';
import { openCookieSettings } from './CookieBanner';

/* ═══════════════════════════════════════════
   ANIMATED BACKGROUND — bats flying
   ═══════════════════════════════════════════ */
const BatParticles = ({ color }) => (
  <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', opacity: 0.06 }}>
    {[...Array(8)].map((_, i) => (
      <div key={i} style={{
        position: 'absolute',
        fontSize: 20 + Math.random() * 30,
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        animation: `floatBat ${8 + Math.random() * 12}s ease-in-out infinite`,
        animationDelay: `${-Math.random() * 10}s`,
        color: color,
      }}>🦇</div>
    ))}
  </div>
);

/* ═══════════════════════════════════════════
   MAIN SERVICE PAGE COMPONENT
   ═══════════════════════════════════════════ */
const ServicePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [hoveredDemo, setHoveredDemo] = useState(null);
  const [hoveredOffering, setHoveredOffering] = useState(null);

  const service = SERVICES[slug];

  if (!service) {
    return (
      <div style={{
        minHeight: '100vh', background: 'rgba(13,14,18,0.95)',
        display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center',
        fontFamily: "'Space Grotesk', sans-serif", color: '#f8f8f2',
      }}>
        <div style={{ fontSize: 80, marginBottom: 20 }}>🧛</div>
        <h1 style={{ fontSize: 32, marginBottom: 12 }}>404 — Serviciu negăsit</h1>
        <p style={{ color: '#8b90a8', marginBottom: 32 }}>Vampirul nostru nu a găsit această pagină.</p>
        <Link to="/" style={{
          padding: '12px 32px', background: '#bd93f9', borderRadius: 8,
          color: '#282a36', fontWeight: 700, textDecoration: 'none',
        }}>← Înapoi la castel</Link>
      </div>
    );
  }

  const { icon, title, tagline, accent, accentAlt, heroQuote, offerings, demos, tech, process } = service;
  const otherServices = Object.entries(SERVICES).filter(([k]) => k !== slug);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'transparent',
      color: '#f8f8f2',
      fontFamily: "'Space Grotesk', 'Segoe UI', sans-serif",
      overflowX: 'hidden',
    }}>

      {/* Dark overlay — more opaque for readability */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 0,
        background: 'rgba(13,14,18,0.92)',
        pointerEvents: 'none',
      }} />

      {/* ═══ GLOBAL STYLES ═══ */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=Crimson+Pro:ital,wght@0,400;0,600;1,400&family=JetBrains+Mono:wght@400;500&display=swap');

        @keyframes floatBat {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          25% { transform: translate(30px, -20px) rotate(5deg); }
          50% { transform: translate(-20px, 15px) rotate(-3deg); }
          75% { transform: translate(15px, -30px) rotate(4deg); }
        }
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 20px ${accent}15; }
          50% { box-shadow: 0 0 40px ${accent}30; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>

      {/* ═══ HERO SECTION ═══ */}
      <section style={{
        position: 'relative',
        padding: 'clamp(56px,12vw,120px) clamp(16px,5vw,24px) clamp(56px,10vw,80px)',
        background: `radial-gradient(ellipse at 30% 20%, ${accent}08 0%, transparent 50%),
                     radial-gradient(ellipse at 70% 80%, ${accentAlt}06 0%, transparent 50%)`,
        borderBottom: `1px solid ${accent}15`,
        overflow: 'hidden',
        zIndex: 1,
      }}>
        <BatParticles color={accent} />

        <div style={{ maxWidth: 900, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Link to="/" style={{
            display: 'flex', width: 'fit-content', alignItems: 'center', gap: 8,
            color: '#8b90a8', textDecoration: 'none', fontSize: 14,
            marginBottom: 40, fontFamily: "'JetBrains Mono', monospace",
            transition: 'color 0.2s',
          }}
          onMouseEnter={e => e.target.style.color = accent}
          onMouseLeave={e => e.target.style.color = '#8b90a8'}>
            ← draculadev.com
          </Link>

          <div style={{
            display: 'inline-block', fontSize: 64, marginBottom: 20,
            animation: 'slideUp 0.6s ease-out',
          }}>{icon}</div>

          <h1 style={{
            fontSize: 'clamp(36px, 6vw, 64px)',
            fontWeight: 700, lineHeight: 1.1, marginBottom: 16,
            animation: 'slideUp 0.6s ease-out 0.1s both',
          }}>
            <span style={{ color: accent }}>//</span> {title}
          </h1>

          <p style={{
            fontSize: 'clamp(16px, 2.5vw, 22px)',
            color: '#c0c4d8',
            maxWidth: 600, lineHeight: 1.5, marginBottom: 32,
            animation: 'slideUp 0.6s ease-out 0.2s both',
          }}>{tagline}</p>

          <blockquote style={{
            borderLeft: `3px solid ${accent}40`,
            paddingLeft: 20,
            fontFamily: "'Crimson Pro', Georgia, serif",
            fontStyle: 'italic', fontSize: 18,
            color: '#8b90a8',
            maxWidth: 500,
            animation: 'slideUp 0.6s ease-out 0.3s both',
          }}>"{heroQuote}"</blockquote>
        </div>
      </section>

      {/* ═══ OFFERINGS ═══ */}
      <section style={{ padding: 'clamp(56px,12vw,80px) clamp(16px,5vw,24px)', maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <h2 style={{
          fontSize: 'clamp(22px,6vw,28px)', fontWeight: 700, marginBottom: 12,
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          <span style={{ color: accent }}>$</span> Ce oferim
        </h2>
        <div style={{
          height: 2, width: 60, background: `linear-gradient(90deg, ${accent}, transparent)`,
          marginBottom: 40,
        }} />

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))',
          gap: 20,
        }}>
          {offerings.map((offer, i) => (
            <div key={i}
              onMouseEnter={() => setHoveredOffering(i)}
              onMouseLeave={() => setHoveredOffering(null)}
              style={{
                background: hoveredOffering === i
                  ? `linear-gradient(135deg, ${accent}12, ${accentAlt}08)`
                  : 'rgba(19,20,26,0.95)',
                border: `1px solid ${hoveredOffering === i ? accent + '50' : '#2a2d3a'}`,
                borderRadius: 12, padding: '28px 24px',
                transition: 'all 0.3s ease',
                transform: hoveredOffering === i ? 'translateY(-4px)' : 'none',
                animation: `slideUp 0.5s ease-out ${0.1 * i}s both`,
                cursor: 'default',
              }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>{offer.icon}</div>
              <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#f8f8f2' }}>
                {offer.name}
              </h3>
              <p style={{ color: '#8b90a8', fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
                {offer.desc}
              </p>
              <span style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 13, color: accent, fontWeight: 600,
                padding: '4px 12px',
                background: `${accent}15`,
                borderRadius: 6,
              }}>{offer.price}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ DEMO PORTFOLIO ═══ */}
      <section style={{
        padding: 'clamp(56px,12vw,80px) clamp(16px,5vw,24px)',
        background: `linear-gradient(180deg, transparent, ${accent}03, transparent)`,
        position: 'relative', zIndex: 1,
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(22px,6vw,28px)', fontWeight: 700, marginBottom: 12,
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            <span style={{ color: accent }}>{'>'}</span> Portofoliu & Demo-uri
          </h2>
          <div style={{
            height: 2, width: 60,
            background: `linear-gradient(90deg, ${accent}, transparent)`,
            marginBottom: 40,
          }} />

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',
            gap: 20,
          }}>
            {demos.map((demo, i) => (
              <div key={i}
                onMouseEnter={() => setHoveredDemo(i)}
                onMouseLeave={() => setHoveredDemo(null)}
                style={{
                  position: 'relative',
                  background: 'rgba(19,20,26,0.95)',
                  borderRadius: 12, overflow: 'hidden',
                  border: `1px solid ${hoveredDemo === i ? accent + '40' : '#2a2d3a'}`,
                  transition: 'all 0.3s ease',
                  transform: hoveredDemo === i ? 'translateY(-6px)' : 'none',
                  boxShadow: hoveredDemo === i ? `0 16px 40px ${accent}15` : 'none',
                  animation: `fadeIn 0.5s ease-out ${0.08 * i}s both`,
                }}>
                <div style={{
                  height: 140,
                  background: `linear-gradient(135deg, ${accent}18, ${accentAlt}12)`,
                  display: 'flex', justifyContent: 'center', alignItems: 'center',
                  fontSize: 56,
                  transition: 'transform 0.3s ease',
                  transform: hoveredDemo === i ? 'scale(1.05)' : 'scale(1)',
                }}>
                  {demo.img}
                </div>

                <div style={{
                  position: 'absolute', top: 12, right: 12,
                  padding: '3px 10px', borderRadius: 6,
                  background: `${accent}25`, color: accent,
                  fontSize: 11, fontWeight: 600,
                  fontFamily: "'JetBrains Mono', monospace",
                  backdropFilter: 'blur(8px)',
                }}>{demo.type}</div>

                <div style={{ padding: '20px 20px 24px' }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#f8f8f2' }}>
                    {demo.title}
                  </h3>
                  <p style={{ color: '#8b90a8', fontSize: 13, lineHeight: 1.5 }}>
                    {demo.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PROCESS TIMELINE ═══ */}
      <section style={{ padding: 'clamp(56px,12vw,80px) clamp(16px,5vw,24px)', maxWidth: 900, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <h2 style={{
          fontSize: 'clamp(22px,6vw,28px)', fontWeight: 700, marginBottom: 12,
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          <span style={{ color: accent }}>~</span> Cum lucrăm
        </h2>
        <div style={{
          height: 2, width: 60,
          background: `linear-gradient(90deg, ${accent}, transparent)`,
          marginBottom: 48,
        }} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {process.map((p, i) => (
            <div key={i} style={{
              display: 'flex', gap: 24, alignItems: 'flex-start',
              animation: `slideUp 0.5s ease-out ${0.15 * i}s both`,
            }}>
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                flexShrink: 0,
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: `${accent}18`,
                  border: `2px solid ${accent}50`,
                  display: 'flex', justifyContent: 'center', alignItems: 'center',
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 14, fontWeight: 700, color: accent,
                }}>
                  {p.step}
                </div>
                {i < process.length - 1 && (
                  <div style={{
                    width: 2, height: 48,
                    background: `linear-gradient(180deg, ${accent}30, transparent)`,
                  }} />
                )}
              </div>

              <div style={{ paddingBottom: i < process.length - 1 ? 24 : 0 }}>
                <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4, color: '#f8f8f2' }}>
                  {p.title}
                </h3>
                <p style={{ color: '#8b90a8', fontSize: 14, lineHeight: 1.5 }}>
                  {p.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ TECH STACK ═══ */}
      <section style={{
        padding: 'clamp(40px,10vw,60px) clamp(16px,5vw,24px)',
        background: `linear-gradient(180deg, transparent, ${accent}04, transparent)`,
        position: 'relative', zIndex: 1,
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <h2 style={{
            fontSize: 22, fontWeight: 700, marginBottom: 24,
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            <span style={{ color: accent }}>{'#'}</span> Tehnologii folosite
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {tech.map((t, i) => (
              <span key={i} style={{
                padding: '8px 16px',
                background: 'rgba(22,24,30,0.95)',
                border: '1px solid #2a2d3a',
                borderRadius: 8, fontSize: 13,
                fontFamily: "'JetBrains Mono', monospace",
                color: '#b0b4c8',
                animation: `fadeIn 0.4s ease-out ${0.05 * i}s both`,
              }}>{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CTA SECTION ═══ */}
      <section style={{ padding: 'clamp(56px,12vw,80px) clamp(16px,5vw,24px)', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <div style={{
          maxWidth: 600, margin: '0 auto', padding: 'clamp(32px,8vw,48px) clamp(20px,5vw,32px)',
          background: `linear-gradient(135deg, ${accent}0a, ${accentAlt}06)`,
          border: `1px solid ${accent}25`,
          borderRadius: 20,
          animation: 'pulseGlow 4s ease-in-out infinite',
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🧄</div>
          <h2 style={{
            fontSize: 26, fontWeight: 700, marginBottom: 12,
            fontFamily: "'Space Grotesk', sans-serif",
          }}>
            Pregătit să începem?
          </h2>
          <p style={{ color: '#8b90a8', fontSize: 15, marginBottom: 28, lineHeight: 1.6 }}>
            Generăm 3 demo-uri gratuit în 30 de secunde.<br/>
            Fără obligații, fără costuri ascunse.
          </p>
          <button
            onClick={() => navigate('/?demo=true')}
            style={{
              padding: '14px clamp(24px,6vw,40px)',
              background: `linear-gradient(135deg, ${accent}, ${accentAlt})`,
              border: 'none', borderRadius: 10,
              color: '#0d0e12', fontWeight: 700, fontSize: 16,
              cursor: 'pointer',
              fontFamily: "'Space Grotesk', sans-serif",
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => {
              e.target.style.transform = 'scale(1.05)';
              e.target.style.boxShadow = `0 8px 30px ${accent}40`;
            }}
            onMouseLeave={e => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = 'none';
            }}
          >
            🧛 Generează Demo Gratuit →
          </button>
        </div>
      </section>

      {/* ═══ OTHER SERVICES ═══ */}
      <section style={{
        padding: 'clamp(40px,10vw,60px) clamp(16px,5vw,24px) clamp(56px,12vw,80px)',
        borderTop: '1px solid #2a2d3a',
        position: 'relative', zIndex: 1,
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <h3 style={{
            fontSize: 16, color: '#b0b4c8', marginBottom: 20,
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            Alte servicii →
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {otherServices.map(([key, s]) => (
              <Link key={key} to={`/servicii/${key}`} style={{
                padding: '10px 20px',
                background: 'rgba(19,20,26,0.95)',
                border: `1px solid ${s.accent}30`,
                borderRadius: 10,
                color: '#e0e2f0',
                textDecoration: 'none',
                fontSize: 14, fontWeight: 500,
                transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = s.accent;
                e.currentTarget.style.background = `${s.accent}15`;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = `${s.accent}30`;
                e.currentTarget.style.background = 'rgba(19,20,26,0.95)';
              }}>
                <span>{s.icon}</span> {s.title}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer style={{
        padding: '32px 24px',
        borderTop: '1px solid #2a2d3a',
        textAlign: 'center',
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 12,
        color: '#8b90a8',
        position: 'relative', zIndex: 1,
      }}>
        © 2026 DraculaDev — Fortăreața ta digitală din Transilvania 🧛
        <button type="button" onClick={openCookieSettings} style={{
          display: 'block', margin: '8px auto 0', background: 'none', border: 'none', padding: 4,
          color: '#6272a4', fontSize: 11, fontFamily: 'inherit', cursor: 'pointer', textDecoration: 'underline',
        }}>Setări cookie-uri</button>
      </footer>
    </div>
  );
};

export default ServicePage;